// Filter / view-by / sort bar — the 2026-08-31 asks: toggle by TEAM and by
// INDIVIDUAL, filter, sort, and the health totals as clickable stats.

import type { InitiativeState, InitiativeStatus, ScheduleHealth } from '../../services/roadmapReports';
import {
  anyFilterOn,
  applyFilters,
  HEALTH_LABELS,
  STATUS_LABELS,
  UNASSIGNED,
  type RoadmapFilters,
  type RoadmapMe,
  type SortKey,
  type TeamColors,
  type ViewBy,
} from './roadmapViewUtils';
import styles from './RoadmapV2.module.css';

interface Props {
  teams: string[];
  teamColors: TeamColors;
  people: string[];
  filters: RoadmapFilters;
  onFilters: (f: RoadmapFilters) => void;
  viewBy: ViewBy;
  onViewBy: (v: ViewBy) => void;
  sort: SortKey;
  onSort: (s: SortKey) => void;
  /** Every initiative; the health totals count the ones the filters keep. */
  all: InitiativeState[];
  shown: number;
  /** View by and Sort only shape the list, so they are drawn only there. */
  grouping?: boolean;
  /** The health tiles count initiatives, so they are not drawn over the questions. */
  tiles?: boolean;
  /** Who the reader is on the roadmap; given → the "Mine" chip (owner ask
   * 2026-10-15: the whole roadmap, "and can filter to mine"). A reader the
   * roadmap does not name yet gets the chip disabled, with the reason. */
  me?: RoadmapMe | null;
}

/** The Mine chip's pressed colour — no team's, since it cuts across teams. */
const MINE_COLOR = '#0f172a';

const HEALTH_ORDER: ScheduleHealth[] = ['late', 'at_risk', 'stuck', 'no_target', 'on_track'];
const STATUS_ORDER: InitiativeStatus[] = [
  'in_progress', 'ongoing', 'blocked', 'waiting', 'proposed', 'unknown', 'parked', 'done', 'dropped',
];

export function RoadmapControls({
  teams, teamColors, people, filters, onFilters, viewBy, onViewBy, sort, onSort, all, shown, grouping = true, tiles = true, me = null,
}: Props) {
  const named = me !== null && (me.names.length > 0 || me.teams.length > 0);
  const toggleTeam = (t: string) => {
    const on = filters.teams.includes(t);
    onFilters({ ...filters, teams: on ? filters.teams.filter((x) => x !== t) : [...filters.teams, t] });
  };
  // The tiles follow the Team / Person / Status / Find filters (they used to
  // show the whole roadmap whatever was filtered); the health filter is left
  // out so clicking a tile narrows the list without zeroing the other tiles.
  const active = applyFilters(all, { ...filters, health: 'all' }, me).filter(
    (i) => i.status !== 'done' && i.status !== 'dropped' && i.status !== 'parked',
  );
  const totals = Object.fromEntries(
    HEALTH_ORDER.map((h) => [h, active.filter((i) => i.health === h).length]),
  ) as Record<ScheduleHealth, number>;

  return (
    <>
      {tiles && (
      <div className={styles.summaryStrip} aria-label="Schedule health totals">
        {HEALTH_ORDER.map((h) => (
          <button
            type="button"
            key={h}
            className={`${styles.stat} ${filters.health === h ? styles.statOn : ''}`}
            onClick={() => onFilters({ ...filters, health: filters.health === h ? 'all' : h })}
            title={`Show only ${HEALTH_LABELS[h].toLowerCase()} initiatives`}
            data-action="roadmap_health_tile"
            data-record-value="health"
            value={h}
          >
            <strong>{totals[h]}</strong> {HEALTH_LABELS[h]}
          </button>
        ))}
        <span className={`${styles.stat}`} style={{ cursor: 'default' }}>
          <strong>{active.length}</strong> active
        </span>
      </div>
      )}

      <div className={styles.controls} aria-label="Roadmap filters">
        {me !== null && (
          <div className={styles.group}>
            <button
              type="button"
              className={`${styles.chip} ${filters.mine ? styles.chipOn : ''}`}
              style={filters.mine ? { background: MINE_COLOR } : undefined}
              onClick={() => onFilters({ ...filters, mine: !filters.mine })}
              aria-pressed={filters.mine}
              disabled={!named}
              title={
                named
                  ? `Only what names you (${me.names.join(', ') || 'no roster name'}) or your teams (${me.teams.join(', ') || 'none'})`
                  : 'The roadmap does not name you yet, so there is nothing of yours to narrow to'
              }
              data-action="roadmap_filter_mine"
            >
              <span className={styles.dot} style={{ background: filters.mine ? '#fff' : MINE_COLOR }} />
              Mine
            </button>
          </div>
        )}
        <div className={styles.group}>
          <label>Team</label>
          {teams.length === 0 && <span className={styles.muted}>none tagged yet</span>}
          {teams.map((t) => {
            const on = filters.teams.includes(t);
            const color = teamColors[t];
            return (
              <button
                type="button"
                key={t}
                className={`${styles.chip} ${on ? styles.chipOn : ''}`}
                style={on ? { background: color } : undefined}
                onClick={() => toggleTeam(t)}
                aria-pressed={on}
                data-action="roadmap_filter_team"
                data-record-value="team"
                value={t}
              >
                <span className={styles.dot} style={{ background: on ? '#fff' : color }} />
                {t}
              </button>
            );
          })}
        </div>

        <div className={styles.group}>
          <label htmlFor="roadmap-person">Person</label>
          <select
            id="roadmap-person"
            data-action="roadmap_filter_person"
            data-record-value="person"
            value={filters.person ?? ''}
            onChange={(e) => onFilters({ ...filters, person: e.target.value || null })}
          >
            <option value="">Everyone</option>
            {people.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
            <option value={UNASSIGNED}>{UNASSIGNED}</option>
          </select>
        </div>

        <div className={styles.group}>
          <label htmlFor="roadmap-status">Status</label>
          <select
            id="roadmap-status"
            data-action="roadmap_filter_status"
            data-record-value="status"
            value={filters.status}
            onChange={(e) => onFilters({ ...filters, status: e.target.value as RoadmapFilters['status'] })}
          >
            <option value="all">Any</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div className={styles.group}>
          <label htmlFor="roadmap-text">Find</label>
          <input
            id="roadmap-text"
            type="text"
            placeholder="title, person, team…"
            value={filters.text}
            onChange={(e) => onFilters({ ...filters, text: e.target.value })}
          />
        </div>

        {grouping && (
          <>
          <div className={styles.group}>
            <label>Group</label>
            <span className={styles.segment} role="group" aria-label="View by">
              {(['list', 'team', 'mandate', 'person', 'actions'] as ViewBy[]).map((v) => (
                <button
                  type="button"
                  key={v}
                  className={viewBy === v ? styles.segmentOn : ''}
                  onClick={() => onViewBy(v)}
                  aria-pressed={viewBy === v}
                  title={v === 'mandate' ? "Group by the owning team's mandate areas (what each task force was formed to do)" : v === 'actions' ? 'Every sub-action as its own row, with all its details' : undefined}
                  data-action="roadmap_view_by"
                  data-record-value="view"
                  value={v}
                >
                  {v === 'list' ? 'Ungrouped' : v === 'team' ? 'By team' : v === 'mandate' ? 'By mandate' : v === 'person' ? 'By person' : 'Sub-actions'}
                </button>
              ))}
            </span>
          </div>

          <div className={styles.group}>
            <label htmlFor="roadmap-sort">Sort</label>
            <select
              id="roadmap-sort"
              data-action="roadmap_sort"
              data-record-value="sort"
              value={sort}
              onChange={(e) => onSort(e.target.value as SortKey)}
            >
              <option value="attention">Needs attention</option>
              <option value="target">Target date</option>
              <option value="updated">Last movement</option>
              <option value="title">Title</option>
            </select>
          </div>
          </>
        )}

        <label className={styles.group}>
          <input
            type="checkbox"
            data-action="roadmap_filter_include_closed"
            data-record-value="include_closed"
            checked={filters.includeClosed}
            onChange={(e) => onFilters({ ...filters, includeClosed: e.target.checked })}
          />
          show done / dropped
        </label>

        <span className={styles.count}>
          {tiles && <>{shown} of {all.length} shown</>}
          {anyFilterOn(filters) && (
            <>
              {tiles && ' · '}
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => {
                  onFilters({ ...filters, teams: [], person: null, status: 'all', health: 'all', text: '', mine: false });
                  onViewBy('list');
                }}
                data-action="roadmap_filters_clear"
              >
                clear filters
              </button>
            </>
          )}
        </span>
      </div>
    </>
  );
}
